import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Plus, Edit2, Trash2, Copy, Check, Mail, 
  FileText, ChevronDown, ChevronUp, Star, StarOff 
} from 'lucide-react';
import { emailTemplatesApi } from '../services/api';
import toast from 'react-hot-toast';

// Available placeholders
const PLACEHOLDERS = [
  { key: '{{kundenname}}', label: 'Kundenname', desc: 'Name des Ansprechpartners' },
  { key: '{{kundenfirma}}', label: 'Kundenfirma', desc: 'Firmenname des Kunden' },
  { key: '{{firmenname}}', label: 'Firmenname', desc: 'Ihr Firmenname' },
  { key: '{{firmenemail}}', label: 'Firmen-E-Mail', desc: 'Ihre E-Mail-Adresse' },
  { key: '{{absendername}}', label: 'Absendername', desc: 'Ihr Name' },
  { key: '{{angebotsnummer}}', label: 'Angebotsnummer', desc: 'z.B. WD-20251217-XXXX' },
  { key: '{{angebotslink}}', label: 'Angebotslink', desc: 'Link zum Online-Angebot' },
  { key: '{{passwort}}', label: 'Passwort-Info', desc: 'PLZ oder Custom-Passwort' },
  { key: '{{gesamtbetrag}}', label: 'Gesamtbetrag', desc: 'Einmalige Kosten' },
  { key: '{{monatlichekosten}}', label: 'Monatliche Kosten', desc: 'Falls vorhanden' },
];

const EmailTemplateManager = ({ 
  companyId, 
  companyInfo, 
  customerInfo, 
  quoteNumber, 
  viewUrl,
  calculatePricing,
  onSelectTemplate,
  passwordProtected,
  customPassword
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPlaceholders, setShowPlaceholders] = useState({ subject: false, body: false });
  const [showEditor, setShowEditor] = useState(false);
  
  // Refs für Cursor-Position
  const subjectInputRef = useRef(null);
  const bodyTextareaRef = useRef(null);
  
  // Form state for editing/creating
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    isDefault: false
  });

  useEffect(() => {
    loadTemplates();
  }, [companyId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await emailTemplatesApi.getAll(companyId);
      setTemplates(response.data);
      
      // Select default template
      const defaultTemplate = response.data.find(t => t.isDefault);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate);
        onSelectTemplate?.(defaultTemplate);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  };

  const replacePlaceholders = (text) => {
    if (!text) return '';
    
    let result = text;
    result = result.replace(/\{\{kundenname\}\}/g, customerInfo?.name || 'Kunde');
    result = result.replace(/\{\{kundenfirma\}\}/g, customerInfo?.company || 'Firma');
    result = result.replace(/\{\{firmenname\}\}/g, companyInfo?.name || 'Ihr Unternehmen');
    result = result.replace(/\{\{firmenemail\}\}/g, companyInfo?.email || 'email@firma.de');
    result = result.replace(/\{\{absendername\}\}/g, companyInfo?.contactPerson || companyInfo?.name || 'Ihr Team');
    result = result.replace(/\{\{angebotsnummer\}\}/g, quoteNumber || 'XXXX');
    result = result.replace(/\{\{angebotslink\}\}/g, viewUrl || '[Link wird generiert]');
    
    // Passwort-Info (dynamisch)
    let passwordInfo = '';
    if (passwordProtected) {
      if (customPassword) {
        passwordInfo = `Das Passwort für den Zugang lautet: ${customPassword}`;
      } else {
        passwordInfo = 'Das Passwort für den Zugang ist Ihre Postleitzahl.';
      }
    }
    result = result.replace(/\{\{passwort\}\}/g, passwordInfo);
    
    result = result.replace(/\{\{gesamtbetrag\}\}/g, 
      `€${(calculatePricing?.einmaligTotal || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}`
    );
    
    // Monthly costs only if present
    if (calculatePricing?.monatlichTotal > 0) {
      result = result.replace(/\{\{monatlichekosten\}\}/g, 
        `Monatliche Kosten: €${calculatePricing.monatlichTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}/Monat`
      );
    } else {
      result = result.replace(/\{\{monatlichekosten\}\}/g, '');
    }
    
    return result;
  };

  const handleSaveTemplate = async () => {
    try {
      if (!formData.name || !formData.subject || !formData.body) {
        toast.error('Bitte füllen Sie alle Felder aus');
        return;
      }

      if (editingTemplate) {
        await emailTemplatesApi.update(editingTemplate.id, formData);
        toast.success('Vorlage aktualisiert');
      } else {
        await emailTemplatesApi.create({ ...formData, companyId });
        toast.success('Vorlage erstellt');
      }
      
      loadTemplates();
      setShowEditor(false);
      setEditingTemplate(null);
      setFormData({ name: '', subject: '', body: '', isDefault: false });
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    
    try {
      await emailTemplatesApi.delete(templateId);
      toast.success('Vorlage gelöscht');
      loadTemplates();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleSetDefault = async (template) => {
    try {
      // Remove default from all
      for (const t of templates) {
        if (t.isDefault) {
          await emailTemplatesApi.update(t.id, { isDefault: false });
        }
      }
      // Set new default
      await emailTemplatesApi.update(template.id, { isDefault: true });
      toast.success('Standardvorlage gesetzt');
      loadTemplates();
    } catch (error) {
      toast.error('Fehler beim Setzen der Standardvorlage');
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    onSelectTemplate?.(template);
  };

  const startEditing = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      isDefault: template.isDefault
    });
    setShowEditor(true);
  };

  const startCreating = () => {
    setEditingTemplate(null);
    setFormData({ name: '', subject: '', body: '', isDefault: false });
    setShowEditor(true);
  };

  const insertPlaceholder = (placeholder, field = 'body') => {
    if (field === 'subject') {
      const input = subjectInputRef.current;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const currentValue = formData.subject || '';
        const newValue = currentValue.substring(0, start) + placeholder + currentValue.substring(end);
        setFormData(prev => ({ ...prev, subject: newValue }));
        // Cursor nach dem eingefügten Platzhalter setzen
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + placeholder.length, start + placeholder.length);
        }, 0);
      } else {
        setFormData(prev => ({ ...prev, subject: (prev.subject || '') + placeholder }));
      }
    } else {
      const textarea = bodyTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart || 0;
        const end = textarea.selectionEnd || 0;
        const currentValue = formData.body || '';
        const newValue = currentValue.substring(0, start) + placeholder + currentValue.substring(end);
        setFormData(prev => ({ ...prev, body: newValue }));
        // Cursor nach dem eingefügten Platzhalter setzen
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
        }, 0);
      } else {
        setFormData(prev => ({ ...prev, body: (prev.body || '') + placeholder }));
      }
    }
  };

  const copyEmailToClipboard = () => {
    if (!selectedTemplate) return;
    
    const emailText = `Betreff: ${replacePlaceholders(selectedTemplate.subject)}\n\n${replacePlaceholders(selectedTemplate.body)}`;
    navigator.clipboard.writeText(emailText);
    toast.success('E-Mail in Zwischenablage kopiert');
  };

  if (loading) {
    return <div className="text-center py-4">Lade Vorlagen...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Template Selection */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          E-Mail-Vorlage
        </h3>
        <Button size="sm" variant="outline" onClick={startCreating}>
          <Plus className="w-4 h-4 mr-1" />
          Neue Vorlage
        </Button>
      </div>

      {/* Template List */}
      <div className="grid gap-2">
        {templates.map((template) => (
          <div
            key={template.id}
            className={`p-3 border rounded-lg cursor-pointer transition-all ${
              selectedTemplate?.id === template.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => {
              // Klick auf Leiste: direkt in Bearbeitungsmodus öffnen
              startEditing(template);
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-slate-500" />
                <span className="font-medium">{template.name}</span>
                {template.isDefault && (
                  <Badge variant="secondary" className="text-xs">Standard</Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); handleSetDefault(template); }}
                  title={template.isDefault ? 'Ist Standardvorlage' : 'Als Standard setzen'}
                >
                  {template.isDefault ? <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" /> : <StarOff className="w-4 h-4" />}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); startEditing(template); }}
                  title="Vorlage bearbeiten"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                  className="text-red-500 hover:text-red-700"
                  title="Vorlage löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {selectedTemplate?.id === template.id && (
              <p className="text-sm text-slate-500 mt-1 truncate">
                Betreff: {template.subject}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Preview */}
      {selectedTemplate && !showEditor && (
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Vorschau</CardTitle>
              <Button size="sm" onClick={copyEmailToClipboard}>
                <Copy className="w-4 h-4 mr-1" />
                E-Mail kopieren
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="font-medium text-slate-800 mb-2">
                Betreff: {replacePlaceholders(selectedTemplate.subject)}
              </p>
              <div className="border-t pt-3 whitespace-pre-wrap text-sm text-slate-700">
                {replacePlaceholders(selectedTemplate.body)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Editor */}
      {showEditor && (
        <Card className="mt-4 border-2 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Vorlagenname</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="z.B. Standard Angebot"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium">Betreff</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPlaceholders(prev => ({ ...prev, subject: !prev.subject }))}
                >
                  Platzhalter {showPlaceholders.subject ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                </Button>
              </div>
              
              {showPlaceholders.subject && (
                <div className="bg-slate-50 p-3 rounded-lg mb-2 grid grid-cols-2 gap-2">
                  {PLACEHOLDERS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => insertPlaceholder(p.key, 'subject')}
                      className="text-left text-xs p-2 bg-white rounded border hover:bg-blue-50 hover:border-blue-300"
                    >
                      <span className="font-mono text-blue-600">{p.key}</span>
                      <span className="block text-slate-500">{p.desc}</span>
                    </button>
                  ))}
                </div>
              )}
              
              <Input
                ref={subjectInputRef}
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="z.B. Ihr Angebot von {{firmenname}}"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium">E-Mail-Text</label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPlaceholders(prev => ({ ...prev, body: !prev.body }))}
                >
                  Platzhalter {showPlaceholders.body ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                </Button>
              </div>
              
              {showPlaceholders.body && (
                <div className="bg-slate-50 p-3 rounded-lg mb-2 grid grid-cols-2 gap-2">
                  {PLACEHOLDERS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => insertPlaceholder(p.key, 'body')}
                      className="text-left text-xs p-2 bg-white rounded border hover:bg-blue-50 hover:border-blue-300"
                    >
                      <span className="font-mono text-blue-600">{p.key}</span>
                      <span className="block text-slate-500">{p.desc}</span>
                    </button>
                  ))}
                </div>
              )}
              
              <textarea
                ref={bodyTextareaRef}
                value={formData.body}
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                placeholder="E-Mail-Text eingeben..."
                className="w-full h-64 px-3 py-2 border rounded-lg text-sm"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
              />
              <label htmlFor="isDefault" className="text-sm">Als Standardvorlage setzen</label>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleSaveTemplate} className="flex-1">
                <Check className="w-4 h-4 mr-1" />
                Speichern
              </Button>
              <Button variant="outline" onClick={() => setShowEditor(false)}>
                Abbrechen
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EmailTemplateManager;
